import { Component, Prop } from 'vue-property-decorator'
import Vue, { CreateElement } from '@src/vue'

import { Body1, H6, Overline, Subtitle1, Subtitle2 } from '@src/elements/typography/typography'

import { Card } from '@src/components/card/card'
import { Icon } from '@src/elements/icon/icon'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { Modal } from '@src/elements/modal/modal'
import { Button } from '@src/elements/button/button'
import { DailyWorkoutResult } from '@src/routes/members/components/results-history/history'
import WorkoutData from '@src/data/workout-data'
import { Rating } from '@src/elements/rating/rating'
import SessionManager from '@src/lib/session_manager'
import { WorkoutService } from '@src/service/workout'
import { convertToLocalDate } from '@src/util/date-format'
import { ExerciseCardHistory } from '@src/components/card/exercise-card/exercise-card-history/exercise-card-history'

import '../workout-card.sass'
import './workout-card-history.sass'
import { MemberProfile } from '@src/data/member-profile'

@Component({
  computed: {
    availableExercises () {
      return this.$store.state.availableExercises
    }
  }
})
export class WorkoutCardHistory extends Vue {

  @Prop({ required: true })
  workout: WorkoutData

  @Prop({ required: true })
  result: DailyWorkoutResult

  @Prop({ required: true })
  date: string

  @Prop({ required: false, default: 'custom' })
  type: 'home' | 'custom'

  @Prop({ required: true })
  changeIntensity: (resultId, value: number) => void

  @Prop({ required: false, default: null })
  memberProfile: MemberProfile

  protected showConfirmPopup: boolean = false
  protected showEquipmentPopup: boolean = false
  private isExpanded: boolean = false
  private showRating = false
  private currentRating = 0

  constructor () {
    super()
  }

  render (h: CreateElement) {
    return <Card class='workout-card workout-card-history'>
      {this.renderHeader(h, 'header', 'header')}
      {this.renderHeader(h, 'tooltips', 'card-portrait-tooltips')}
      {this.renderPreview(h)}
      {this.renderTitle(h)}
      {this.renderPortraitTitle(h)}
      {this.renderSpecs(h)}
      {this.renderRight(h)}
      {this.renderExpanded(h)}
    </Card>
  }

  renderHeader (h: CreateElement, slot: string, className: string = '') {
    return <Overline slot={slot} class={`${className}`}>
      <div class='tracked'>
        {this.workout.isCertified() ? <span class='certified-icon'><Icon name='workout/certified' />{this.$t('PAGE.CONTENT.WORKOUTS.TYPES.CERTIFIED_WORKOUT')} /&nbsp;</span> : ''}
        {this.workout.isCertified() ? <span class='device-certified-icon'><Icon name='workout/certified' /></span> : ''}
        {this.workout.circleWorkout ?
          <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.CIRCULAR_WORKOUT')}><Icon class='workout-type-icon' name='workout/circle-workout' /></Tooltip> :
          this.workout.continuousWorkout ?
            <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.CONTINUOUS_WORKOUT')}><Icon class='workout-type-icon' name='workout/continuous-workout' /></Tooltip> :
            <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.NON_CONTINUOUS_WORKOUT')}><Icon class='workout-type-icon' name='workout/non-continuous-workout' /></Tooltip>
        }
        {this.workout.stationLocked ?
          <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.STATION_DEPENDENT_WORKOUT')}><Icon class='workout-type-icon' name='workout/station-dependent-workout' /></Tooltip> :
          <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.STATION_INDEPENDENT_WORKOUT')}><Icon class='workout-type-icon' name='workout/station-independent-workout' /></Tooltip>}
      </div>
    </Overline>
  }

  renderPreview (h: CreateElement) {
    return <div slot='preview' class='workout-preview-big'>
      <img src={this.workout.displayImage} />
    </div>
  }

  renderTitle (h: CreateElement) {
    return <H6 class='title' slot='title'>
      {this.workout.name}
    </H6>
  }

  renderPortraitTitle (h: CreateElement) {
    return (
      <div class={`portrait-workout-card-title ${!this.workout.equipment.length && 'justify-items-center'}`} slot='title'>
        <H6 class='title' >
        {this.workout.name}
        </H6>
        {this.renderSpecsEquipment(h)}
      </div>
    )
  }

  renderSpecs (h: CreateElement) {
    const shouldShowRatingBtn = !this.result.intensityRating && !SessionManager.Instance.isManager()
    return <div slot='specs' class='specs'>
      {this.renderSpecsEquipment(h)}
      {shouldShowRatingBtn && <Button click={() => this.showRating = true} type='outline' label={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.RATING.RATE')} class='rating-btn'/>}
      {this.showRating && this.renderRatingPopup(h)}
    </div>
  }

  renderSpecsEquipment (h: CreateElement) {
    const firstItemsEquipment = this.workout.equipment.slice(0, 5).map((equipmentName) => {
      return <div class='equipment'>
        <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipmentName.toUpperCase())}>
          <Icon name={'equipment/' + equipmentName} />
        </Tooltip>
      </div>
    })

    const equipment = this.workout.equipment.map((equipmentName) => {
      return <div class='equipment'>
        <Icon name={'equipment/' + equipmentName} />
        <Body1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipmentName.toUpperCase())}</Body1>
      </div>
    })

    return <div>
      <div class='equipmentWrapper'>{firstItemsEquipment}{this.workout.equipment.length > 5 ?
        <Icon class='see-more' name='util/see-more' click={this.invertShowEquipment} /> : ''} </div>
      <Modal show={this.showEquipmentPopup} class={'training-equipment-modal'} closeCallback={this.invertShowEquipment}
             title={this.$t('EQUIPMENT')}>
        {equipment}
      </Modal>
    </div>
  }

  renderRight (h: CreateElement) {
    return <div class='right' slot='right'>
      <div class='workout-date'><Subtitle2>{this.date}</Subtitle2></div>
      {!this.isExpanded ?
        <Button click={() => this.isExpanded = !this.isExpanded} type='remove' label={this.$t('MORE')} class='expand' icon='util/caret-down' />
      :
        <Button click={() => this.isExpanded = !this.isExpanded} type='remove' label={this.$t('LESS')} class='collapse' icon='util/caret-down' />
      }
    </div>
  }

  renderRatingPopup (h: CreateElement) {
    return <Modal show={true} title={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.RATING.RATE')} closeCallback={() => this.showRating = false} cssClass='rating-modal'>
      <span class='question'>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.RATING.QUESTION')}</span>
      <div class='rating-number'>{!!this.currentRating && this.currentRating}</div>
      <div class='rating-description'>{!!this.currentRating && this.$t(`PAGE.MEMBERS.TRAINING.HISTORY.RATING.${this.currentRating}`)}</div>
      <Rating max={10} currentValue={this.currentRating} selectRating={this.selectRating}/>
      <div slot='footer' class='btn-row'>
        <Button type='elevated' class='send' align='center' label={this.$t('PAGE.MESSAGING.SEND')} click={this.sendRating} disabled={!this.currentRating}/>
      </div>
    </Modal>
  }

  sendRating () {
    this.changeIntensity(convertToLocalDate(this.result.day), this.currentRating)
    this.showRating = false
    return new WorkoutService().rateWorkout(this.result.id, this.currentRating)
  }

  selectRating (value: number) {
    this.currentRating = value
  }

  renderExpanded (h: CreateElement) {
    const shouldShowRatingBtn = !this.result.intensityRating && !SessionManager.Instance.isManager()
    if (this.isExpanded) {
      if (this.type === 'home') {
        return <div class='results' slot='expanded'>
          {this.result.exerciseResults.map(exerciseResult => <ExerciseCardHistory type={this.type} exerciseResult={exerciseResult} renderColorByValue={this.renderColorByValue} workout={this.workout} />) }
          {/* the results are only rendered while expanded */}
          {this.isExpanded ?
            <Button class='collapse-btn' click={() => this.isExpanded = !this.isExpanded} type='remove' label={this.$t('LESS')} icon='util/caret-up' /> : /* istanbul ignore next */ ''}
        </div>
      } else {
        return <div class='results' slot='expanded'>
          <div class={`results-row`}>
            <div class='result'>
              <Icon name='workout/exercises' />
              <Subtitle1>{`${this.$t('EXERCISES')}`}</Subtitle1>
              <span>
              <Subtitle1>{`${this.result.exerciseCount || 0}`}</Subtitle1>
            </span>
            </div>
            <div class='result'>
              <Icon name={'brand/heartbeat'} class={`${this.renderColorByValue((this.result.repetitions || 0) / (this.result.totalRepetitions || 10))}`} />
              <Subtitle1>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REP_SCORE')}`}</Subtitle1>
              <span class={`${this.renderColorByValue((this.result.repetitions || 0) / (this.result.totalRepetitions || 10))}`}>
              <Subtitle1>{`${this.result.repetitions || 0}/${this.result.totalRepetitions || 0}`}</Subtitle1>
            </span>
            </div>
            {this.result.pixformanceScore >= 0 ?
              <div class='result'>
                <Icon name={'workout/score'} class={`${this.renderColorByValue((this.result.pixformanceScore || 0) / 100)}`} />
                <Subtitle1>{`${this.$t('PAGE.MEMBERS.STATUS.PIX_SCORE')}`}</Subtitle1>
                <span class={`${this.renderColorByValue((this.result.pixformanceScore || 0) / 100)}`}>
                <Subtitle1>{`${this.result.pixformanceScore}/100`}</Subtitle1>
              </span>
              </div> : null }
            {this.result.tempoScore >= 0 ?
              <div class='result'>
                <Icon name={'brand/tempo'} class={`${this.renderColorByValue((this.result.tempoScore || 0) / 100)}`} />
                <Subtitle1>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.TEMPO_SCORE')}`}</Subtitle1>
                <span class={`${this.renderColorByValue((this.result.tempoScore || 0) / 100)}`}>
                <Subtitle1>{`${this.result.tempoScore}/100`}</Subtitle1>
              </span>
              </div> : null }
            {this.result.precisionScore >= 0 ?
              <div class='result'>
                <Icon name={'brand/accuracy'} class={`${this.renderColorByValue((this.result.precisionScore || 0) / 100)}`} />
                <Subtitle1>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.ACCURACY')}`}</Subtitle1>
                <span class={`${this.renderColorByValue((this.result.precisionScore || 0) / 100)}`}>
                <Subtitle1>{`${this.result.precisionScore}/100`}</Subtitle1>
              </span>
              </div> : null }
            {this.result.rangeScore >= 0 ?
              <div class='result'>
                <Icon name={'brand/range-of-motion'} class={`${this.renderColorByValue((this.result.rangeScore || 0) / 100)}`} />
                <Subtitle1>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.RANGE_OF_MOTION')}`}</Subtitle1>
                <span class={`${this.renderColorByValue((this.result.rangeScore || 0) / 100)}`}>
                <Subtitle1>{`${this.result.rangeScore}/100`}</Subtitle1>
              </span>
              </div> : null }
            {this.result.intensityRating ?
              <div class='result'>
                <Icon name={'flags/staleWorkoutAlert'} class={this.renderIRColorByValue(this.result.intensityRating)} />
                <Subtitle1>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.RATING.INTENSITY_RATE')}</Subtitle1>
                <span class={this.renderIRColorByValue(this.result.intensityRating)}>
                <Subtitle1>{`${this.result.intensityRating}/10`}</Subtitle1>
              </span>
              </div> : null }
          </div>
          <div class={'results-icons-wrapper'}>
            <div class='history-flex-container'/>
            <div class='icons'>
              <Icon name={'brand/heartbeat'} />
              <Icon name={'workout/score'} />
              <Icon name={'brand/tempo'} />
              <Icon name={'brand/accuracy'} />
              <Icon name={'brand/range-of-motion'} />
            </div>
            <div class='history-flex-container'/>
          </div>
          <div class='exercise-result-wrapper'>
            {this.result.exerciseResults.map(exerciseResult => <ExerciseCardHistory type={this.type} exerciseResult={exerciseResult} renderColorByValue={this.renderColorByValue} workout={this.workout} />)}
          </div>
          {shouldShowRatingBtn && (<div class='mobile-workout-rate'>
            <Button click={() => this.showRating = true} type='outline'
                    label={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.RATING.RATE')} class='rating-btn'/>
          </div>)}
          {/* the results are only rendered while expanded */}
          {this.isExpanded ?
            <Button class='collapse-btn' click={() => this.isExpanded = !this.isExpanded} type='remove' label={this.$t('LESS')} icon='util/caret-up' /> : /* istanbul ignore next */ ''}
        </div>
      }
    }
  }

  renderColorByValue (val: number) {
    if (val >= 0 && val < 0.7) {
      return 'red'
    }
    if (val >= 0.7 && val < 0.85) {
      return 'orange'
    }
    return 'cyan'
  }

  renderIRColorByValue (val: number) {
    if (val <= 3) return 'red'
    if (val <= 6) return 'orange'
    if (val <= 8) return 'cyan'
    return 'red'
  }

  invertShowEquipment () {
    this.showEquipmentPopup = !this.showEquipmentPopup
  }
}
